import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

// Shared, nesting-aware overlay z-index lives in the design system so DS and UI overlays stack
// correctly against each other during migration; see design-system/src/lib/overlay-z-index.tsx.
import { OverlayLayerProvider, useOverlayZIndex } from "@tellescope/design-system";

import { cn } from "../lib/utils";

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  style: styleProp,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  // Nesting-aware z-index: a popover inside a dialog stacks above it; a dialog opened from this
  // popover starts a fresh stack above the popover. See overlay-z-index.tsx.
  const { z, style, className: zClassName } = useOverlayZIndex("popover");

  return (
    <PopoverPrimitive.Portal>
      <OverlayLayerProvider base={z}>
        <PopoverPrimitive.Content
          data-slot="popover-content"
          align={align}
          sideOffset={sideOffset}
          // Merge, so a caller passing `style` (e.g. width) can't clobber the `--z` custom property
          // that drives nesting-aware stacking.
          style={{ ...style, ...styleProp }}
          className={cn(
            "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-72 origin-(--radix-popover-content-transform-origin) rounded-[10px] border p-4 shadow-md outline-hidden",
            zClassName,
            className
          )}
          {...props}
        />
      </OverlayLayerProvider>
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
